<template>
  <v-app id="inspire" :class="isDark ? 'theme--dark' : 'theme--light'">
    <!-- ══════════════════════════════════════
         PREMIUM SIDEBAR
    ══════════════════════════════════════ -->
    <v-navigation-drawer
      v-model="drawer"
      app
      :mini-variant="!$vuetify.breakpoint.smAndDown && mini"
      :mini-variant-width="64"
      :width="260"
      :temporary="$vuetify.breakpoint.smAndDown"
      :permanent="!$vuetify.breakpoint.smAndDown"
      class="premium-nav-drawer"
      :class="{ 'light-sidebar': !isDark }"
      :style="
        isDark
          ? `background:#080D1A !important; border-right:1px solid rgba(255,255,255,.05);`
          : `background:#ffffff !important; border-right:1px solid #E5E7EB;`
      "
    >
      <!-- Logo -->
      <div
        class="sb-logo-wrap"
        :style="`background-color: ${primaryColor};`"
        :class="{ 'sb-logo-mini': mini }"
      >
        <div
          class="sb-logo-mark"
          style="cursor: pointer"
        >
          <template v-if="user.logo_url && !imageError">
            <img
              :src="API_BASE_URL + '/uploads/' + user.logo_url"
              :alt="user.name"
              style="
                border-radius: 50%;
                width: 100%;
                height: 100%;
                object-fit: contain;
              "
              @error="imageError = true"
            />
          </template>
          <template v-else>
            {{ user.name ? user.name.charAt(0).toUpperCase() : "A" }}
          </template>
        </div>
        <div class="sb-logo-text sb-label">
          <div class="sb-logo-name" style="font-size: 16px">
            {{ user.name || "Admin Portal" }}
          </div>
          <div class="sb-logo-sub">Society Management</div>
        </div>

        <!-- Desktop Toggle Button -->
        <!-- <button
          v-if="!$vuetify.breakpoint.smAndDown"
          class="sb-desktop-toggle"
          @click="toggleDrawer"
          :style="{
            background: isDark ? '#1e293b' : '#ffffff',
            borderColor: isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'
          }"
        >
          <v-icon size="14" color="var(--brand)">
            {{ isMini ? 'mdi-chevron-right' : 'mdi-chevron-left' }}
          </v-icon>
        </button> -->
      </div>

      <!-- Nav -->
      <div class="sb-nav-scroll">
        <div v-for="group in filteredNavGroups" :key="group.label">
          <div class="nav-grp-label sb-label">{{ group.label }}</div>
          <v-divider v-if="mini" class="my-1" style="opacity: 0.05" />

          <template v-for="item in group.items">
            <!-- Items with children -->
            <v-list-group
              v-if="item.childrens && hasPermission(item)"
              :key="'grp-' + item.title"
              :value="
                item.catchRoutes &&
                item.catchRoutes.some((r) =>
                  $router.currentRoute.path.startsWith(r),
                )
              "
              no-action
              class="sb-list-group"
            >
              <template v-slot:activator>
                <v-tooltip right :disabled="!mini" :color="primaryColor">
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      class="sb-nav-item"
                      style="
                        padding-left: 20px;
                        width: 100%;
                        display: flex;
                        align-items: center;
                        gap: 10px;
                      "
                      v-bind="attrs"
                      v-on="on"
                      @click="mini ? navigate(item.childrens[0].route) : null"
                    >
                      <v-icon style="font-size: 20px">{{ item.icon }}</v-icon>
                      <span
                        class="sb-label"
                        style="font-size: 13px; font-weight: 500; flex: 1"
                        >{{ item.title }}</span
                      >
                    </div>
                  </template>
                  <span>{{ item.title }}</span>
                </v-tooltip>
              </template>

              <v-tooltip
                v-for="child in item.childrens"
                :key="child.title"
                right
                :disabled="!mini"
                :color="primaryColor"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="sb-nav-item sb-nav-child"
                    :class="{ active: isNavActive(child.route) }"
                    @click="navigate(child.route)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon style="font-size: 14px">{{ child.icon }}</v-icon>
                    <span class="sb-label">{{ child.title }}</span>
                  </div>
                </template>
                <span>{{ child.title }}</span>
              </v-tooltip>
            </v-list-group>

            <!-- Simple items -->
            <v-tooltip
              v-else-if="!item.childrens && hasPermission(item)"
              :key="'item-' + item.title"
              right
              :disabled="!mini"
              :color="primaryColor"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="sb-nav-item"
                  :class="{ active: isNavActive(item.route) }"
                  @click="navigate(item.route)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <!-- In mini mode show badge on icon; in full mode show badge chip -->
                  <v-badge
                    v-if="item.badge && isMini"
                    color="red"
                    :content="item.badge"
                    overlap
                    offset-x="6"
                    offset-y="6"
                  >
                    <v-icon style="font-size: 20px">{{ item.icon }}</v-icon>
                  </v-badge>
                  <v-icon v-else style="font-size: 20px">{{ item.icon }}</v-icon>
                  <span class="sb-label" style="flex: 1">{{ item.title }}</span>
                  <span v-show="item.badge && !isMini" class="sb-badge sb-label">{{
                    item.badge
                  }}</span>
                </div>
              </template>
              <span>{{ item.title }}</span>
            </v-tooltip>
          </template>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- ══════════════════════════════════════
         PREMIUM TOP BAR
    ══════════════════════════════════════ -->
    <v-app-bar
      app
      elevation="0"
      class="premium-app-bar"
      :color="primaryColor"
      :style="`border-bottom: 1px solid ${
        isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,0.05)'
      };`"
      height="64"
      dark
    >
      <!-- Hamburger -->
      <v-app-bar-nav-icon @click="toggleDrawer" color="#ffffff" class="mr-2" />

      <!-- Page Title -->
      <div style="display: flex; flex-direction: column; gap: 1px">
        <div style="display: flex; align-items: flex-end; gap: 14px">
          <div
            v-if="user.contact_person_name"
            style="
              font-size: 14px;
              font-weight: 500;
              color: #fff !important;
              padding-bottom: 2px;
              margin-bottom: 1px;
            "
          >
            Welcome, {{ user.contact_person_name }}
          </div>
        </div>
        <div
          class="pg-sub"
          style="color: #fff !important; opacity: 0.8; font-size: 11.5px"
        >
          {{ currentDate }}
        </div>
      </div>

      <v-spacer />

      <!-- Right controls -->
      <div style="display: flex; align-items: center; gap: 8px">
        <!-- Notifications -->
        <div
          class="topbar-ic-btn"
          @click="$router.push('/societyadmin/chat')"
          title="Chat"
        >
          <v-badge
            v-if="unreadChatCount > 0"
            color="red"
            overlap
            offset-x="10"
            offset-y="10"
            :content="unreadChatCount"
          >
            <v-icon style="font-size: 17px">mdi-chat-outline</v-icon>
          </v-badge>
          <v-icon v-else style="font-size: 17px">mdi-chat-outline</v-icon>
        </div>

        <!-- Notifications Bell -->
        <v-menu v-model="notificationMenu" transition="slide-y-transition" bottom offset-y right :close-on-content-click="false" max-width="380">
          <template v-slot:activator="{ on, attrs }">
            <div class="topbar-ic-btn" v-bind="attrs" v-on="on" title="Notifications">
              <v-badge
                v-if="unreadCount > 0"
                color="#ef4444"
                overlap
                offset-x="10"
                offset-y="10"
                :content="unreadCount > 99 ? '99+' : unreadCount"
              >
                <v-icon style="font-size: 17px">mdi-bell-outline</v-icon>
              </v-badge>
              <v-icon v-else style="font-size: 17px">mdi-bell-outline</v-icon>
            </div>
          </template>

          <div class="notif-panel">
            <!-- Header -->
            <div class="notif-header">
              <div class="notif-header-left">
                <v-icon size="16" :color="primaryColor" class="mr-2">mdi-bell-ring-outline</v-icon>
                <span class="notif-title">Notifications</span>
                <span
                  v-if="unreadCount > 0"
                  class="notif-count-chip"
                  :style="`background:${primaryColor}`"
                >{{ unreadCount }}</span>
              </div>
              <div class="notif-header-actions" v-if="notifications.length > 0">
                <button
                  v-if="unreadCount > 0"
                  class="notif-action-btn"
                  :style="`color:${primaryColor}`"
                  @click="markAllRead"
                  :disabled="markingAllRead"
                  title="Mark all as read"
                >
                  <v-icon size="12" class="mr-1" :color="primaryColor">mdi-check-all</v-icon>
                  {{ markingAllRead ? '…' : 'Read all' }}
                </button>
                <button
                  class="notif-action-btn notif-clear-btn"
                  @click="clearAllNotifications"
                  :disabled="clearingAll"
                  title="Clear all"
                >
                  <v-icon size="12" class="mr-1" color="#ef4444">mdi-delete-outline</v-icon>
                  {{ clearingAll ? '…' : 'Clear all' }}
                </button>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="notifications.length === 0" class="notif-empty">
              <div class="notif-empty-icon" :style="`border-color:${primaryColor}30`">
                <v-icon size="36" :color="`${primaryColor}60`">mdi-bell-off-outline</v-icon>
              </div>
              <div class="notif-empty-text">You're all caught up!</div>
              <div class="notif-empty-sub">No new notifications</div>
            </div>

            <!-- List -->
            <div v-else class="notif-list">
              <div
                v-for="item in notifications"
                :key="item.id"
                class="notif-item"
                :class="{ 'notif-item--unread': !item.is_read }"
                @click="handleNotificationClick(item)"
              >
                <!-- Icon -->
                <div
                  class="notif-icon-wrap"
                  :style="item.type === 'MEMBER_REQUEST'
                    ? `background: linear-gradient(135deg, ${primaryColor}, ${lightenColor(primaryColor)})`
                    : `background: linear-gradient(135deg, ${primaryColor}cc, ${primaryColor})`"
                >
                  <v-icon size="16" color="white">
                    {{ item.type === 'MEMBER_REQUEST' ? 'mdi-account-clock-outline' : 'mdi-face-agent' }}
                  </v-icon>
                </div>

                <div class="notif-body">
                  <div class="notif-item-title" :class="{ 'font-weight-bold': !item.is_read }">{{ item.title }}</div>
                  <div class="notif-item-desc">{{ item.description }}</div>
                  <div class="notif-item-time">
                    <v-icon size="10" class="mr-1" color="#94a3b8">mdi-clock-outline</v-icon>
                    {{ formatNotificationTime(item.created_on) }}
                  </div>
                </div>

                <!-- Unread dot -->
                <div v-if="!item.is_read" class="notif-unread-dot" :style="`background:${primaryColor}`"></div>

                <button class="notif-dismiss" @click.stop="deleteNotification(item.id)" title="Dismiss">
                  <v-icon size="14" color="#94a3b8">mdi-close</v-icon>
                </button>
              </div>
            </div>
          </div>
        </v-menu>



        <!-- Profile dropdown -->
        <v-menu transition="slide-y-transition" bottom offset-y right>
          <template v-slot:activator="{ on, attrs }">
            <div
              class="user-av-premium"
              v-bind="attrs"
              v-on="on"
              style="cursor: pointer"
              :style="`background: linear-gradient(135deg, ${lightenColor(
                primaryColor,
              )}, ${primaryColor});`"
            >
              <template v-if="user.logo_url && !imageError">
                <img
                  :src="API_BASE_URL + '/uploads/' + user.logo_url"
                  :alt="user.name"
                  style="
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    object-fit: contain;
                  "
                  @error="imageError = true"
                />
              </template>
              <template v-else>
                {{ user.name ? user.name.charAt(0).toUpperCase() : "A" }}
              </template>
            </div>
          </template>

          <v-list
            dense
            nav
            class="py-1"
            :color="isDark ? '#162035' : 'white'"
            style="
              min-width: 200px;
              border-radius: 12px;
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
              border: 1px solid var(--border);
            "
          >
            <v-list-item @click="openProfile" class="rounded-lg mb-1">
              <v-list-item-icon
                ><v-icon small>mdi-account-outline</v-icon></v-list-item-icon
              >
              <v-list-item-title style="font-size: 13px"
                >Profile</v-list-item-title
              >
            </v-list-item>
            <v-list-item :to="'/societyadmin/theme'" class="rounded-lg mb-1">
              <v-list-item-icon
                ><v-icon small>mdi-palette-outline</v-icon></v-list-item-icon
              >
              <v-list-item-title style="font-size: 13px"
                >Theme</v-list-item-title
              >
            </v-list-item>
            <v-list-item @click="dialogFeedback = true" class="rounded-lg mb-1">
              <v-list-item-icon
                ><v-icon small>mdi-comment-outline</v-icon></v-list-item-icon
              >
              <v-list-item-title style="font-size: 13px"
                >Feedback</v-list-item-title
              >
            </v-list-item>
            <v-divider class="my-1" />
            <v-list-item
              @click="showLogoutConfirm = true"
              class="rounded-lg"
              style="color: #ef4444"
            >
              <v-list-item-icon
                ><v-icon small style="color: #ef4444"
                  >mdi-logout</v-icon
                ></v-list-item-icon
              >
              <v-list-item-title style="font-size: 13px; color: #ef4444"
                >Logout</v-list-item-title
              >
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </v-app-bar>

    <!-- ══════════════════════════════════════
         FEEDBACK DIALOG
    ══════════════════════════════════════ -->
    <PremiumModal
      v-model="dialogFeedback"
      title="Feedback / Suggestion"
      max-width="560"
      :loading="loadingFeedback"
      submit-label="Send Feedback"
      @submit="submitFeedbackForm"
    >
      <FeedbackModal
        ref="feedbackComp"
        @loading="loadingFeedback = $event"
        @close="dialogFeedback = false"
      />
    </PremiumModal>

    <!-- Global Alert -->
    <Alert />

    <!-- Profile Modal -->
    <PremiumModal
      v-model="dialogProfile"
      title="My Society Profile"
      max-width="650"
      :loading="loadingProfile"
      submit-label="Submit"
      @submit="submitProfile"
    >
      <SocietyAdminProfile
        ref="profileComp"
        isModal
        @loading="loadingProfile = $event"
        @close="dialogProfile = false"
      />
    </PremiumModal>

    <!-- Main Content -->
    <v-main :style="`background: ${isDark ? '#080E1B' : '#F8FAFC'}; overflow-x: hidden;`">
      <transition name="fade-up" mode="out-in">
        <router-view />
      </transition>
    </v-main>

    <ConfirmModal
      v-model="showLogoutConfirm"
      type="logout"
      title="Logout?"
      message="Are you sure you want to logout from your account?"
      confirmText="Yes, Logout"
      cancelText="Cancel"
      :loading="logoutLoading"
      @confirm="handleLogoutConfirm"
    />
  </v-app>
</template>

<script>
import io from "socket.io-client";
import moment from "moment";
import Alert from "../../Alert.vue";
import { mapMutations, mapState } from "vuex";
import PremiumModal from "../../common/PremiumModal.vue";
import SocietyAdminProfile from "../Profile.vue";
import FeedbackModal from "../FeedbackModal.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";

export default {
  name: "PremiumLayout",

  components: {
    Alert,
    PremiumModal,
    SocietyAdminProfile,
    FeedbackModal,
    ConfirmModal,
  },

  data() {
    return {
      socket: null,
      drawer: true,
      mini: false,
      mobileExpanded: false,
      showLogoutConfirm: false,
      logoutLoading: false,
      dialogFeedback: false,
      loadingFeedback: false,
      dialogProfile: false,
      loadingProfile: false,
      feedback: { title: "", description: "", picture: null },
      serverSideErrors: {},
      user: { name: "", email: "", logo_url: null, has_multiple_building: null },
      imageError: false,
      isExpired: [],
      notifications: [],
      notificationMenu: false,
      clearingAll: false,
      markingAllRead: false,
      notificationInterval: null,

      navGroups: [
        {
          label: "Overview",
          items: [
            {
              title: "Dashboard",
              icon: "mdi-grid-large",
              route: "/societyadmin/dashboard",
            },
          ],
        },
        {
          label: "Residents",
          items: [
            {
              title: "Block Management",
              icon: "mdi-domain",
              route: "/societyadmin/block",
              requiresMultiBuilding: true,
            },
            {
              title: "Member Requests",
              icon: "mdi-account-clock-outline",
              route: "/societyadmin/member/requests",
            },
            {
              title: "Members",
              icon: "mdi-account-multiple-outline",
              route: "/societyadmin/member/list",
            },
            {
              title: "Committee Members",
              icon: "mdi-account-tie-outline",
              route: "/societyadmin/committemember",
            },
            {
              title: "Request Approval",
              icon: "mdi-account-check-outline",
              route: "/societyadmin/responsibleperson",
            },
          ],
        },
        {
          label: "Communication",
          items: [
            {
              title: "Announcements",
              icon: "mdi-bullhorn-outline",
              route: "/societyadmin/announcement",
            },
            {
              title: "Events",
              icon: "mdi-calendar-month-outline",
              route: "/societyadmin/event",
            },
            {
              title: "Notices",
              icon: "mdi-text-box-search-outline",
              route: "/societyadmin/notice",
            },
            {
              title: "Galleries",
              icon: "mdi-view-grid-plus-outline",
              route: "/societyadmin/gallery",
            },
            {
              title: "Chat",
              icon: "mdi-chat-processing-outline",
              route: "/societyadmin/chat",
            },
          ],
        },
        {
          label: "Finance",
          items: [
            {
              title: "Bill Master",
              icon: "mdi-file-certificate-outline",
              route: "/societyadmin/bills",
            },
            {
              title: "Bills",
              icon: "mdi-bank-outline",
              route: "/societyadmin/bills/house",
            },
            {
              title: "Expenses",
              icon: "mdi-card-text-outline",
              route: "/societyadmin/expense",
            },
            {
              title: "Expense Categories",
              icon: "mdi-view-grid-outline",
              route: "/societyadmin/category",
            },
          ],
        },
        {
          label: "Operations",
          items: [
            {
              title: "Visitors",
              icon: "mdi-account-arrow-right-outline",
              route: "/societyadmin/visitor",
            },
            {
              title: "Staff Members",
              icon: "mdi-badge-account-outline",
              route: "/societyadmin/staff",
            },
            {
              title: "Security Guards",
              icon: "mdi-shield-account-outline",
              route: "/societyadmin/securityguard",
            },
            {
              title: "Help Desk",
              icon: "mdi-face-agent",
              route: "/societyadmin/complaints",
            },
          ],
        },
        {
          label: "Settings",
          items: [
            {
              title: "Permissions",
              icon: "mdi-shield-key-outline",
              route: "/societyadmin/permissions",
            },
            {
              title: "My Plan",
              icon: "mdi-diamond-stone",
              route: "/societyadmin/plan",
            },
          ],
        },
      ],
    };
  },

  created() {
    // Synchronize CSS variables immediately from store state
    this.syncThemeVars(this.primaryColor);
    this.initDrawerState();
    this.getSocietyDetail();
    this.getUserPlan();
    this.initSocket();
    this.fetchNotifications();
    this.notificationInterval = setInterval(() => {
      this.fetchNotifications();
    }, 15000);
    this.registerDeviceToken();
  },
  
  beforeDestroy() {
    if (this.socket) {
      this.socket.disconnect();
    }
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
    }
  },

  watch: {
    $route(to, from) {
      // Only refetch if the base route segment changes
      const toBase = to.path.split("/")[2];
      const fromBase = from.path.split("/")[2];
      if (toBase !== fromBase) {
        this.getSocietyDetail();
      }
    },
    primaryColor(val) {
      if (!val) return;
      // Keep CSS var in sync when theme changes
      document.documentElement.style.setProperty("--brand", val);
      document.documentElement.style.setProperty("--brand-lt", `${val}1A`);
      document.documentElement.style.setProperty("--brand-glow", `${val}2E`);
      document.documentElement.style.setProperty("--sb-act-bdr", val);
      document.documentElement.style.setProperty("--sb-act-bg", `${val}22`);
    },
    buttonColor(val) {
      if (!val) return;
      // Keep --btn-color CSS var in sync with selected button color
      document.documentElement.style.setProperty("--btn-color", val);
      document.documentElement.style.setProperty("--btn-color-lt", `${val}1A`);
      document.documentElement.style.setProperty(
        "--btn-color-glow",
        `${val}2E`,
      );
    },
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    ...mapState("society", ["unreadChatCount", "hasMultipleBuilding"]),

    unreadCount() {
      return this.notifications.filter(n => !n.is_read).length;
    },

    // mini-variant: always mini on mobile/tablet unless user expanded, user-controlled on desktop
    isMini() {
      if (this.$vuetify.breakpoint.smAndDown) return false;
      return this.mini;
    },

    filteredNavGroups() {
      return this.navGroups
        .map((group) => ({
          ...group,
          items: group.items
            .filter((item) => {
              if (this.isExpired === true) {
                return item.title === "My Plan" || item.title === "Dashboard";
              }
              if (
                item.requiresMultiBuilding &&
                this.hasMultipleBuilding !== 1
              ) {
                return false;
              }
              return true;
            })
            .map((item) => {
              if (item.title === "Chat") {
                return {
                  ...item,
                  badge: this.unreadChatCount > 0 ? this.unreadChatCount : null,
                };
              }
              return item;
            }),
        }))
        .filter((g) => g.items.length > 0);
    },

    pageTitle() {
      const routeTitleMap = {
        "/societyadmin/dashboard": "Dashboard",
        "/societyadmin/bills": "Bill Master",
        "/societyadmin/bills/house": "Bills",
        "/societyadmin/expense": "Expenses",
        "/societyadmin/category": "Expense Categories",
        "/societyadmin/announcement": "Announcements",
        "/societyadmin/event": "Events",
        "/societyadmin/notice": "Notices",
        "/societyadmin/gallery": "Gallery",
        "/societyadmin/chat": "Chat",
        "/societyadmin/block": "Block Management",
        "/societyadmin/member/requests": "Member Requests",
        "/societyadmin/member/list": "Members",
        "/societyadmin/committemember": "Committee Members",
        "/societyadmin/responsibleperson": "Request Approvals",
        "/societyadmin/visitor": "Visitors",
        "/societyadmin/staff": "Staff",
        "/societyadmin/securityguard": "Security Guards",
        "/societyadmin/complaints": "Help Desk",
        "/societyadmin/permissions": "Permissions",
        "/societyadmin/plan": "My Plan",
        "/societyadmin/profile": "Profile",
        "/societyadmin/theme": "Theme Settings",
      };
      const path = this.$route?.path || "";
      for (const [key, val] of Object.entries(routeTitleMap)) {
        if (path === key || path.startsWith(key + "/")) return val;
      }
      return "iSmartSociety";
    },

    currentDate() {
      const d = new Date();
      return d.toLocaleDateString("en-IN", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("society", ["setSocietyConfig", "setUnreadChatCount"]),
    ...mapMutations("theme", [
      "setDarkMode",
      "setPrimaryColor",
      "setButtonColor",
      "resetTheme",
    ]),

    initSocket() {
      if (this.socket) {
        this.socket.disconnect();
      }

      let token = "";
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        token = user?.token || "";
      } catch (e) {
        console.error("Error getting token:", e);
      }

      if (!token) return;

      const url = process.env.VUE_APP_API_URL || "http://localhost:3333";
      this.socket = io(url, {
        auth: { token },
        query: { token },
      });

      this.socket.on("connect", () => {
        console.log("[Layout Socket] Connected successfully");
      });

      this.socket.on("message:new", (msg) => {
        // Only increment unread count for messages sent by others (not admin themselves)
        if (msg.sender_type !== "admin") {
          // If we are currently on the chat page, Chat.vue handles its own unread states and mutations.
          // Otherwise, increment the global unread badge in layout header instantly.
          if (this.$route.path !== "/societyadmin/chat") {
            const currentCount = this.$store.state.society.unreadChatCount || 0;
            this.setUnreadChatCount(currentCount + 1);
          }
        }
      });
    },

    handleLogoutConfirm() {
      this.logoutLoading = true;

      this.$axios
        .post("/societyadmin/logout")
        .then((response) => {
          let data = response.data;

          if (data.status) {
            if (this.socket) {
              this.socket.disconnect();
              this.socket = null;
            }
            localStorage.removeItem("user");
            localStorage.removeItem("flag");
            localStorage.removeItem("selectedTab");
            localStorage.removeItem("isPlanExpired");

            // Clear cross-port cookie for front web
            document.cookie = "iss_logged_in=; path=/; domain=.ismartsociety.com; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure";

            // Broadcast logout event for front web tabs
            if (typeof BroadcastChannel !== 'undefined') {
              try {
                const bc = new BroadcastChannel('iss_auth');
                bc.postMessage('logout');
                bc.close();
              } catch (e) {
                console.error(e);
              }
            }

            // Reset theme
            this.resetTheme();

            this.$vuetify.theme.themes.light.primary = "#f87676";
            this.$vuetify.theme.themes.light.secondary = "#30384d";
            this.$vuetify.theme.themes.dark.primary = "#f87676";
            this.$vuetify.theme.themes.dark.secondary = "#30384d";

            this.syncThemeVars("#f87676");
            document.documentElement.style.setProperty(
              "--btn-color",
              "#30384d",
            );

            this.$router.push({ path: "/societyadmin/login" });
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: 2000,
              message: data.message,
            });
          }
        })
        .finally(() => {
          this.logoutLoading = false;
          this.showLogoutConfirm = false;
        });
    },
    submitFeedbackForm() {
      if (this.$refs.feedbackComp) {
        this.$refs.feedbackComp.submitFeedback();
      }
    },
    openProfile() {
      // Wait for v-menu to fully close before opening dialog
      this.$nextTick(() => {
        setTimeout(() => {
          this.dialogProfile = true;
        }, 50);
      });
    },

    hasPermission(item) {
      if (this.isExpired === true) {
        return item.title === "My Plan" || item.title === "Dashboard";
      }
      if (
        item.requiresMultiBuilding &&
        this.hasMultipleBuilding !== 1
      ) {
        return false;
      }
      return true;
    },

    navigate(route) {
      if (this.$router.currentRoute.path !== route) {
        this.$router.push({ path: route });
      }
      // On mobile/tablet: close the temporary drawer after navigation
      if (this.$vuetify.breakpoint.smAndDown) {
        this.drawer = false;
      }
    },

    toggleDrawer() {
      if (this.$vuetify.breakpoint.smAndDown) {
        // Mobile/tablet: toggle drawer visibility
        this.drawer = !this.drawer;
      } else {
        // Desktop: toggle between mini icon-strip and full sidebar
        this.mini = !this.mini;
      }
    },

    initDrawerState() {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.drawer = false;
      } else {
        this.drawer = true;
      }
      this.mini = false;
      this.mobileExpanded = false;
    },

    /** Simple hex color darkening for sidebar glow */
    darkenColor(hex) {
      if (!hex || !hex.startsWith("#")) return "#cc5555";
      try {
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);
        r = Math.max(0, r - 30);
        g = Math.max(0, g - 30);
        b = Math.max(0, b - 30);
        return `#${r.toString(16).padStart(2, "0")}${g
          .toString(16)
          .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
      } catch {
        return hex;
      }
    },

    lightenColor(hex) {
      if (!hex || !hex.startsWith("#")) return "#ff9090";
      try {
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);
        r = Math.min(255, r + 40);
        g = Math.min(255, g + 40);
        b = Math.min(255, b + 40);
        return `#${r.toString(16).padStart(2, "0")}${g
          .toString(16)
          .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
      } catch {
        return hex;
      }
    },

    closeView() {
      this.dialogFeedback = false;
      this.feedback = { title: "", description: "", picture: null };
    },

    getUserPlan() {
      let _self = this;
      _self.$axios.get("societyadmin/user-plan").then((response) => {
        let data = response.data;
        if (data.status) {
          _self.isExpired = data.isExpired;
          if (!data.isExpired) {
            localStorage.removeItem("isPlanExpired");
          } else {
            localStorage.setItem("isPlanExpired", "true");
          }
        }
      });
    },

    logout() {
      let _self = this;
      _self.$axios.post("/societyadmin/logout").then((response) => {
        let data = response.data;
        if (data.status) {
          localStorage.removeItem("user");
          localStorage.removeItem("flag");
          localStorage.removeItem("selectedTab");

          // Notify web frontend of logout (BroadcastChannel + clear cookie)
          document.cookie = "iss_logged_in=; path=/; domain=.ismartsociety.com; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure";
          try {
            const bc = new BroadcastChannel("iss_auth");
            bc.postMessage("logout");
            bc.close();
          } catch (e) { /* BroadcastChannel not supported */ }

          // Reset Theme to defaults
          this.resetTheme();

          // Reset Vuetify theme colors to system defaults
          this.$vuetify.theme.themes.light.primary = "#f87676";
          this.$vuetify.theme.themes.light.secondary = "#30384d";
          this.$vuetify.theme.themes.dark.primary = "#f87676";
          this.$vuetify.theme.themes.dark.secondary = "#30384d";

          // Reset CSS variables
          this.syncThemeVars("#f87676");
          document.documentElement.style.setProperty("--btn-color", "#30384d");

          this.$router.push({ path: "/societyadmin/login" });
        } else {
          _self.setAlert({
            showAlert: true,
            variant: "red accent-3",
            timeout: "2000",
            message: data.message,
          });
        }
      });
    },

    submitProfile() {
      if (this.$refs.profileComp) {
        this.$refs.profileComp.updateProfile();
      }
    },

    submitForm() {
      let _self = this;
      const formData = new FormData();
      for (const key in this.feedback) {
        if (Object.prototype.hasOwnProperty.call(this.feedback, key)) {
          formData.append(key, this.feedback[key]);
        }
      }
      _self.$axios.post("societyadmin/feedback/", formData).then((response) => {
        let data = response.data;
        if (data.status) {
          this.dialogFeedback = false;
          this.setAlert({
            showAlert: true,
            variant: "green accent-4",
            timeout: "2000",
            message: data.message,
          });
        } else {
          if (!data.errors) {
            this.setAlert({
              showAlert: true,
              message: data.message,
              variant: "red accent-3",
              timeout: 2000,
            });
          } else {
            _self.serverSideErrors = {};
            for (let i = 0; i < data.errors.length; i++) {
              this.serverSideErrors[data.errors[i].field] =
                data.errors[i].message;
            }
          }
        }
      });
    },

    getSocietyDetail() {
      let _self = this;
      // Don't fetch if a dialog is open
      if (_self.dialogProfile || _self.dialogFeedback) return;

      _self.$axios.get("/societyadmin/societyProfile").then((response) => {
        let data = response.data;
        if (data.status) {
          Object.assign(_self.user, data.society);
          _self.setSocietyConfig({
            hasMultipleBuilding: data.society.has_multiple_building,
            unitType: data.society.type,
          });
          _self.setUnreadChatCount(data.society.unread_chat_count || 0);
          // Sync primary color from user profile
          if (data.society.theme_primary_color) {
            _self.setPrimaryColor({
              primaryColor: data.society.theme_primary_color,
            });
            _self.syncThemeVars(data.society.theme_primary_color);
          }

          if (data.society.theme_button_color) {
            const btnColor = data.society.theme_button_color;
            _self.setButtonColor({ buttonColor: btnColor });
            document.documentElement.style.setProperty("--btn-color", btnColor);
            document.documentElement.style.setProperty(
              "--btn-color-lt",
              `${btnColor}1A`,
            );
            document.documentElement.style.setProperty(
              "--btn-color-glow",
              `${btnColor}2E`,
            );
            _self.$vuetify.theme.themes.light.secondary = btnColor;
            _self.$vuetify.theme.themes.dark.secondary = btnColor;
          }

          // Persist both colors to localStorage so theme is stable on next refresh
          let storedUser = localStorage.getItem("user");
          if (storedUser) {
            try {
              let userObj = JSON.parse(storedUser);
              if (data.society.theme_primary_color)
                userObj.theme_primary_color = data.society.theme_primary_color;
              if (data.society.theme_button_color)
                userObj.theme_button_color = data.society.theme_button_color;
              userObj.name = data.society.name;
              userObj.logo_url = data.society.logo_url;
              localStorage.setItem("user", JSON.stringify(userObj));
            } catch (e) {
              console.error("Failed to update theme in storage", e);
            }
          }
        }
      });
    },

    syncThemeVars(color) {
      if (!color) return;
      document.documentElement.style.setProperty("--brand", color);
      document.documentElement.style.setProperty("--brand-lt", `${color}1A`);
      document.documentElement.style.setProperty("--brand-glow", `${color}2E`);
      document.documentElement.style.setProperty("--sb-act-bdr", color);
      document.documentElement.style.setProperty("--sb-act-bg", `${color}22`);
      // Also set a lighter version for button hovers on dark headers
      document.documentElement.style.setProperty(
        "--topbar-hov",
        "rgba(255,255,255,0.12)",
      );
    },

    /**
     * Checks if a sidebar item should be highlighted as active.
     * Supports sub-paths like /add, /edit, /view for the same module.
     */
    isNavActive(route) {
      if (!route) return false;
      const path = this.$route.path;
      if (path === route) return true;

      // Exception for Bill Master vs Individual Bills
      if (
        route === "/societyadmin/bills" &&
        path.startsWith("/societyadmin/bills/house")
      )
        return false;

      // Determine module base (e.g., /societyadmin/member/list -> /societyadmin/member)
      const moduleBase = route.replace(/\/list$/, "");

      // Check for standard sub-routes
      const subPaths = ["/add", "/edit/", "/view/", "/list/"];
      if (subPaths.some((p) => path.startsWith(moduleBase + p))) {
        return true;
      }

      return path.startsWith(route + "/");
    },

    fetchNotifications() {
      if (this.dialogProfile || this.dialogFeedback) return;
      this.$axios
        .get("societyadmin/notifications")
        .then((response) => {
          const data = response.data;
          if (data.status) {
            this.notifications = data.notifications || [];
          }
        })
        .catch((error) => {
          console.error("Error fetching notifications:", error);
        });
    },

    deleteNotification(id) {
      this.$axios
        .delete(`societyadmin/notifications/${id}`)
        .then((response) => {
          const data = response.data;
          if (data.status) {
            this.notifications = this.notifications.filter((n) => n.id !== id);
          }
        })
        .catch((error) => {
          console.error("Error deleting notification:", error);
        });
    },

    clearAllNotifications() {
      this.clearingAll = true;
      this.$axios
        .delete("societyadmin/notifications/clear-all")
        .then((response) => {
          if (response.data.status) {
            this.notifications = [];
            this.notificationMenu = false;
          }
        })
        .catch((error) => {
          console.error("Error clearing notifications:", error);
        })
        .finally(() => {
          this.clearingAll = false;
        });
    },

    handleNotificationClick(item) {
      // Close the popup immediately
      this.notificationMenu = false;
      // Mark as read if unread
      if (!item.is_read) {
        this.$set(item, 'is_read', 1);
        this.$axios.post(`societyadmin/notifications/${item.id}/read`).catch(() => {});
      }
      // Dismiss the notification
      this.deleteNotification(item.id);

      if (item.type === "MEMBER_REQUEST") {
        const target = "/societyadmin/member/requests";
        if (this.$route.path === target) return;
        this.$router.push({ path: target });
      } else if (item.type === "HELP_DESK") {
        const target = `/societyadmin/complaints/view/${item.type_uuid}`;
        if (this.$route.path === target) return;
        this.$router.push({ path: target });
      }
    },

    markAllRead() {
      this.markingAllRead = true;
      this.$axios
        .post("societyadmin/notifications/mark-all-read")
        .then((response) => {
          if (response.data.status) {
            this.notifications = this.notifications.map(n => ({ ...n, is_read: 1 }));
          }
        })
        .catch((error) => {
          console.error("Error marking all read:", error);
        })
        .finally(() => {
          this.markingAllRead = false;
        });
    },

    formatNotificationTime(timestamp) {
      if (!timestamp) return "";
      return moment.unix(timestamp).fromNow();
    },

    registerDeviceToken() {
      let token = this.$route.query.device_token || localStorage.getItem("device_token");
      if (!token && window.deviceToken) {
        token = window.deviceToken;
      }
      
      if (!token && window.FCM) {
        window.FCM.getToken().then(t => {
          this.sendTokenToBackend(t);
        }).catch(err => console.log(err));
        return;
      }

      if (token) {
        this.sendTokenToBackend(token);
      }
    },
    
    sendTokenToBackend(token) {
      this.$axios.put("societyadmin/token", {
        device_token: token,
        device_type: "web"
      }).then(res => {
        if (res.data.status) {
          localStorage.setItem("device_token", token);
          console.log("Device token registered successfully");
        }
      }).catch(err => {
        console.error("Error registering device token:", err);
      });
    }
  },
};
</script>

<style scoped>
/* ══════════════ SIDEBAR ══════════════ */
.premium-nav-drawer {
  background: #080d1a !important;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Base label transition */
.sb-label {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin 0.3s ease !important;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  vertical-align: middle;
  opacity: 1;
  max-width: 300px; /* Large enough to fit any label */
}

/* Mini state hiding */
.v-navigation-drawer--mini-variant .sb-label {
  opacity: 0 !important;
  max-width: 0 !important;
  margin: 0 !important;
  transform: translateX(-10px);
  pointer-events: none;
}

.sb-logo-wrap {
  height: 64px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
}

.sb-logo-mini {
  padding: 0;
  justify-content: center;
}

.sb-desktop-toggle {
  position: absolute;
  top: 50%;
  right: -13px;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}
.sb-desktop-toggle:hover {
  transform: translateY(-50%) scale(1.1);
}

.sb-logo-mark {
  width: 40px;
  height: 40px;
  border-radius: 50% !important;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #ffffff;
  color: var(--brand);
  font-size: 20px;
  font-weight: 800;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.sb-logo-name {
  font-family: "Bricolage Grotesque", sans-serif;
  font-weight: 700;
  color: #ffffff !important;
  letter-spacing: -0.02em;
}

.sb-logo-sub {
  font-size: 10.5px;
  color: #ffffff !important;
  opacity: 0.85;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
}

.sb-logo-text {
  flex: 1;
  min-width: 0;
}

.sb-nav-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0 6px;
  scrollbar-width: none;
}
.sb-nav-scroll::-webkit-scrollbar {
  display: none;
}

.nav-grp-label {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--sb-sec, rgba(255, 255, 255, 0.22));
  padding: 18px 20px 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sb-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  color: var(--sb-tx, rgba(255, 255, 255, 0.42));
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 3px solid transparent;
  user-select: none;
  text-decoration: none;
}

.v-navigation-drawer--mini-variant .sb-nav-item {
  gap: 0 !important;
  justify-content: center !important;
  padding: 8px 0 !important;
}

.v-navigation-drawer--mini-variant .nav-grp-label {
  display: none !important;
}

.v-navigation-drawer--mini-variant .sb-nav-item .v-icon {
  font-size: 22px !important;
}
.v-navigation-drawer--mini-variant .sb-label {
  display: none !important;
}

.v-navigation-drawer--mini-variant .sb-nav-item.active {
  background: transparent !important;
  border-left-color: transparent !important;
}

.sb-nav-item .v-icon {
  color: var(--sb-tx, rgba(255, 255, 255, 0.42)) !important;
}

.sb-nav-item:hover {
  color: var(--sb-txh, rgba(255, 255, 255, 0.78));
  background: var(--sb-hov, rgba(255, 255, 255, 0.04));
}

.sb-nav-item:hover .v-icon {
  color: var(--sb-txh, rgba(255, 255, 255, 0.78)) !important;
}

.sb-nav-item.active {
  color: var(--sb-act, #ffffff);
  background: var(--sb-act-bg, rgba(248, 118, 118, 0.13));
  border-left-color: var(--brand, #f87676);
  font-weight: 600;
}

.sb-nav-item.active .v-icon {
  color: var(--sb-act, #ffffff) !important;
}

.sb-nav-child {
  padding-left: 44px;
  font-size: 12.5px;
}

.sb-badge {
  margin-left: auto;
  background: #ef4444 !important;
  color: #ffffff !important;
  font-size: 10px;
  font-weight: 700;
  padding: 1.5px 7px;
  border-radius: 20px;
  box-shadow: 0 2px 5px rgba(239, 68, 68, 0.4);
}

/* List group header */
.sb-list-group ::v-deep .v-list-group__header {
  padding: 0 !important;
  min-height: unset !important;
  background: transparent !important;
}
.sb-list-group ::v-deep .v-list-group__header__append-icon {
  color: var(--sb-tx, rgba(255, 255, 255, 0.3)) !important;
  transition: opacity 0.2s ease !important;
}
.v-navigation-drawer--mini-variant
  .sb-list-group
  ::v-deep
  .v-list-group__header__append-icon {
  opacity: 0 !important;
  pointer-events: none;
}

.sb-list-group ::v-deep .v-list-group__items {
  background: var(--sb-bg2, rgba(0, 0, 0, 0.15)) !important;
}

.premium-nav-drawer ::v-deep .v-navigation-drawer__content {
  scrollbar-width: none; /* Firefox */
}
.premium-nav-drawer ::v-deep .v-navigation-drawer__content::-webkit-scrollbar {
  display: none; /* Chrome, Safari */
}

/* Light Mode Overrides for Sidebar */
.light-sidebar {
  --sb-sec: #9ca3af;
  --sb-tx: #4b5563;
  --sb-txh: #111827;
  --sb-hov: rgba(0, 0, 0, 0.03);
  --sb-act: var(--brand);
  --sb-act-bg: var(--brand-lt, rgba(248, 118, 118, 0.1));
  --sb-bdr: #e5e7eb;
  --sb-bg2: #f9fafb;
}

/* ══════════════ TOP BAR ══════════════ */
.pg-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.2;
}
.pg-sub {
  font-size: 11px;
  opacity: 0.55;
  font-weight: 400;
}

/* Theme toggle */
.theme-toggle-pill {
  width: 56px;
  height: 30px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 15px;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 3px 4px;
  transition: all 0.25s;
  flex-shrink: 0;
}
.theme-toggle-pill.dark-mode {
  background: #162035;
  border-color: rgba(255, 255, 255, 0.1);
}
.theme-toggle-thumb {
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.theme-toggle-pill.dark-mode .theme-toggle-thumb {
  transform: translateX(26px);
  background: #1c2a40;
}

/* User avatar */
.user-av-premium {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}
.user-av-premium:hover {
  transform: scale(1.05);
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.15);
}

/* topbar icon btn */
.topbar-ic-btn {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  color: white;
}
.topbar-ic-btn:hover {
  background: var(--topbar-hov, rgba(255, 255, 255, 0.12));
}

/* Route transition */
.fade-up-enter-active {
  animation: fadeUp 0.28s ease;
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* ══════════════ MINI SIDEBAR — RESPONSIVE ══════════════ */

/* Active state in mini mode: show a small pill indicator instead of left border */
.v-navigation-drawer--mini-variant .sb-nav-item.active {
  background: var(--sb-act-bg, rgba(248, 118, 118, 0.18)) !important;
  border-left-color: transparent !important;
  border-radius: 10px;
  margin: 0 8px;
}

/* Divider between groups in mini mode */
.v-navigation-drawer--mini-variant .nav-grp-divider {
  display: block;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 4px 12px;
}

/* Tooltip text for icons in mini mode */
.v-navigation-drawer--mini-variant .sb-nav-item {
  position: relative;
}

/* ══════════════ NOTIFICATION PANEL ══════════════ */
.notif-panel {
  width: 380px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06);
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
}

.notif-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 14px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbff;
}

.notif-header-left {
  display: flex;
  align-items: center;
}

.notif-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.notif-count-chip {
  margin-left: 8px;
  background: var(--brand, #6366f1);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  line-height: 1.6;
}

.notif-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.notif-action-btn {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  background: none;
  border: 1px solid currentColor;
  border-radius: 8px;
  padding: 4px 9px;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0.85;
}
.notif-action-btn:hover { opacity: 1; filter: brightness(0.92); }
.notif-action-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.notif-clear-btn {
  color: #ef4444 !important;
  border-color: #ef4444 !important;
}
.notif-clear-btn:hover { background: #fee2e2 !important; }

.notif-empty {
  padding: 40px 20px;
  text-align: center;
}
.notif-empty-icon {
  width: 64px;
  height: 64px;
  background: #f8fafc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
  border: 2px dashed #e2e8f0;
}
.notif-empty-text {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}
.notif-empty-sub {
  font-size: 12px;
  color: #94a3b8;
}

.notif-list {
  max-height: 420px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
}
.notif-list::-webkit-scrollbar { width: 4px; }
.notif-list::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f8fafc;
  position: relative;
}
.notif-item:last-child { border-bottom: none; }
.notif-item:hover { background: #f8fafc; }
.notif-item--unread { background: var(--brand-lt, #f0f4ff); }
.notif-item--unread:hover { filter: brightness(0.97); }

.notif-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.notif-body {
  flex: 1;
  min-width: 0;
}
.notif-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  margin-bottom: 3px;
}
.notif-item-desc {
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.45;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.notif-item-time {
  display: flex;
  align-items: center;
  font-size: 10.5px;
  color: #94a3b8;
}

.notif-dismiss {
  background: none;
  border: none;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.notif-item:hover .notif-dismiss { opacity: 1; }
.notif-dismiss:hover { background: #fee2e2; }
.notif-dismiss:hover .v-icon { color: #ef4444 !important; }

/* Tablet (≤960px) — start in mini by default */
@media (max-width: 960px) {
  .premium-nav-drawer ::v-deep .v-navigation-drawer__content {
    overflow-x: hidden;
  }
  /* Ensure mini-width matches the 64px we set */
  .v-navigation-drawer--mini-variant .sb-logo-wrap {
    padding: 0 !important;
    justify-content: center !important;
  }
  .v-navigation-drawer--mini-variant .sb-logo-mark {
    width: 36px !important;
    height: 36px !important;
  }
}

/* Mobile (≤600px) — tight mini strip */
@media (max-width: 600px) {
  .sb-nav-item {
    padding: 10px 0 !important;
    justify-content: center !important;
  }
  .v-navigation-drawer--mini-variant .sb-nav-item .v-icon {
    font-size: 20px !important;
  }
  /* Shrink logo area height on mobile */
  .sb-logo-wrap {
    height: 56px !important;
  }
  /* Main content area should account for mini sidebar width */
  .v-main {
    padding-left: 64px !important;
  }
}
</style>
